import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import StepsHeader from './utils/stepsHeader';

import './enterChat.css';
import BasicDropdown from '../../components/inputs/basicDropdown/basicDropdown';
import Btn from '../../components/Btn/Btn';

import { getContractsByID } from '../../api/user';
import { useTranslation } from 'react-i18next';
import { useMessenger } from '../../context/messengerContext';
import { enter_chat } from '../../api/chats';
import { setCookie } from '../../utils/CookieManager';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

export default function EnterChat() {
    const [contracts, setContracts] = useState([]);
    const [selectedContract, setSelectedContract] = useState('');
    const [error, setError] = useState({});
    const [autoSubmit, setAutoSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const { userId, platform } = useMessenger();
    const { t } = useTranslation('basic');
    const location = useLocation();

    const isIOS26Version = isIOSVersionHigherThan26Version();
    const isFromChat =
        Boolean(location.state?.fromChat) ||
        new URLSearchParams(location.search).has('fromChat') ||
        (typeof document !== 'undefined' &&
            document.referrer.includes('/manager_app/user_chat'));

    useEffect(() => {
        if (!userId) return;
        getContractsByID({ platform, userId }).then((res) => {
            if (res) {
                setContracts(
                    res.map((contract_number) => ({
                        label: contract_number,
                        value: contract_number,
                    })),
                );
            }
            setIsLoading(false);
        });
    }, [userId]);

    useEffect(() => {
        if (!contracts.length) return;

        // Check for contract_number query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const contractFromQuery = urlParams.get('contract_number');

        if (contractFromQuery) {
            // Check if the contract from query exists in the fetched contracts
            const contractExists = contracts.some(
                (contract) => contract.value === contractFromQuery,
            );
            if (contractExists) {
                setSelectedContract(contractFromQuery);
                setAutoSubmit(true);
                return;
            }
        }

        // Fallback to auto-select if only one contract
        if (contracts.length === 1) {
            setSelectedContract(contracts[0].value);
            setAutoSubmit(true);
        }
    }, [contracts]);

    useEffect(() => {
        if (!selectedContract) return;
        if (!autoSubmit) return;

        if (isFromChat) return;

        (async () => {
            const data = await enter_chat({
                platform,
                userId: userId ? userId : '0',
                contract_number: selectedContract,
            });

            if (!!data?.result?.error) return;

            setCookie('token', data.token, { expires: 3600 * 24, path: '/' });
            localStorage.setItem('user_id', data.mongo_user_id);
            localStorage.setItem('manager_app_access_token', data.token);
            localStorage.setItem('manager_app_mongo_id', data.mongo_user_id);
            localStorage.setItem('manager_app_chat_id', data.chat_id);
            const chatUrl = `/manager_app/user_chat?access_token=${data.token}&mongo_id=${data.mongo_user_id}&chat_id=${data.chat_id}`;
            window.location.replace(chatUrl);
        })();
    }, [selectedContract, autoSubmit, platform, userId, isFromChat]);

    const renderContent = () => {
        if (isLoading) return null;

        if (contracts.length === 0) {
            return (
                <BasicPanel
                    header={
                        <span className='paragraph-16M'>
                            {t('no_contracts_available')}
                        </span>
                    }
                    className={'gradient_bg'}
                >
                </BasicPanel>
            );
        }

        if (contracts.length >= 2) {
            return (
                <>
                    <BasicDropdown
                        label={t('contract_number')}
                        placeholder={t('select_contract_number')}
                        error={error?.contract_number}
                        data={contracts}
                        onChange={(c) => setSelectedContract(c)}
                        value={selectedContract}
                    />
                    <BasicPanel
                        header={
                            <span className='paragraph-16M'>
                                {t('select_contract_for_chat')}
                            </span>
                        }
                        className={'gradient_bg'}
                    >
                        <Btn
                            text={t('enter_button')}
                            fluid
                            onClick={async () => {
                                if (!selectedContract) {
                                    setError({
                                        contract_number: t(
                                            'select_contract_number',
                                        ),
                                    });
                                    return;
                                }

                                const data = await enter_chat({
                                    contract_number: selectedContract,
                                    platform,
                                    userId: userId ? userId : null,
                                });

                                if (!!data?.result?.error) return;

                                setCookie('token', data.token, {
                                    expires: 3600 * 24,
                                    path: '/',
                                });
                                localStorage.setItem(
                                    'user_id',
                                    data.mongo_user_id,
                                );

                                localStorage.setItem('manager_app_access_token', data.token);
                                localStorage.setItem('manager_app_mongo_id', data.mongo_user_id);
                                localStorage.setItem('manager_app_chat_id', data.chat_id);

                                const chatUrl = `/manager_app/user_chat?access_token=${data.token}&mongo_id=${data.mongo_user_id}&chat_id=${data.chat_id}`;

                                window.location.replace(chatUrl);
                            }}
                        />
                    </BasicPanel>
                </>
            );
        }

        return null;
    };

    return (
        <>
            {!isLoading && contracts.length !== 1 && <StepsHeader />}
            <div
                className={`padded ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
            >
                {renderContent()}
            </div>
        </>
    );
}
